// @flow
import React, { Component, Fragment } from 'react';
import QuoteHead from 'components/quote/QuoteHead';
import QuoteContent from 'components/quote/QuoteContent';
import QuoteTags from 'components/quote/QuoteTags';
import ScrollToTop from 'components/common/ScrollToTop';
import { BaseActions, CommonActions, DisplayActions, QuotesActions } from 'store/actionCreators';
import { connect } from 'react-redux';
import type { State } from 'store';
import type { QuoteData, TocItem } from 'store/modules/quotes';
import QuoteToc from 'components/quote/QuoteToc';
import QuestionModal from 'components/common/QuestionModal/QuestionModal';
import { type ContextRouter, type Location, withRouter } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { convertToPlainText } from 'lib/common';
import QuotePlaceholder from 'components/quote/QuotePlaceholder';
import throttle from 'lodash/throttle';
import QuoteLeftSticker from 'components/quote/QuoteLeftSticker/QuoteLeftSticker';
import QuoteSeriesInfo from 'components/quote/QuoteSeriesInfo/QuoteSeriesInfo';

type Props = {
  username: ?string,
  urlSlug: ?string,
  quote: ?QuoteData,
  toc: ?(TocItem[]),
  activeHeading: ?string,
  likeInProcess: boolean,
  currentUsername: ?string,
  askRemove: boolean,
  routerHistory: Location[],
  shouldCancel: boolean,
  logged: boolean,
} & ContextRouter;

class QuoteViewer extends Component<Props> {
  onActivateHeading = throttle((headingId: string) => {
    QuotesActions.activateHeading(headingId);
  }, 250);

  initialize = async () => {
    // set scroll to top
    // if (document && document.body && document.body.scrollTop) {
    //   document.body.scrollTop = 0;
    // }
    // if (document && document.documentElement) {
    //   document.documentElement.scrollTop = 0;
    // }
    const { username, urlSlug, shouldCancel } = this.props;
    if (!username || !urlSlug) return;
    try {
      if (shouldCancel) return;
      await QuotesActions.readQuote({
        username,
        urlSlug,
      });
      if (!this.props.quote) return;
      const { id } = this.props.quote;
      QuotesActions.getSequences(id);
    } catch (e) {
      console.log(e);
      if (e && e.response && e.response.status === 404) {
        this.props.history.replace('/404');
      }
    }
  };

  componentDidUpdate(prevProps, prevState) {
    if (prevProps.urlSlug !== this.props.urlSlug) {
      QuotesActions.unloadQuote();
      this.initialize();
    }
  }

  componentWillUnmount() {
    QuotesActions.unloadQuote();
  }

  onSetToc = (toc: ?(TocItem[])) => {
    QuotesActions.setToc(toc);
  };

  onToggleLike = () => {
    const { quote, likeInProcess } = this.props;
    if (likeInProcess) return;
    if (!quote) return;
    if (quote.liked) {
      QuotesActions.unlike(quote.id);
    } else {
      QuotesActions.like(quote.id);
    }
  };

  onToggleAskRemove = () => {
    QuotesActions.toggleAskRemove();
  };

  onConfirmRemove = async () => {
    const { quote, history, routerHistory } = this.props;
    QuotesActions.toggleAskRemove();
    if (!quote) return;
    try {
      await CommonActions.removeQuote(quote.id);
      DisplayActions.flushList();
    } catch (e) {
      console.log(e);
    }
    if (routerHistory.length === 0) {
      history.push('/');
      return;
    }
    history.goBack();
  };

  componentDidMount() {
    this.initialize();
    const { hash } = this.props.location;
    if (hash !== '') {
      QuotesActions.activateHeading(decodeURI(hash.split('#')[1]));
    }
  }

  informCopy = () => {
    BaseActions.showToast({
      message: '링크가 복사되었습니다',
      type: 'success',
    });
  };

  render() {
    const { quote, toc, activeHeading, username, currentUsername, askRemove, logged } = this.props;
    const { onSetToc, onActivateHeading } = this;

    if (!quote) return <QuotePlaceholder />;
    if (!quote.user) return <QuotePlaceholder />;
    const plainTextBody = convertToPlainText(quote.body);

    const url = `${process.env.BASE_URL}/@${quote.user.username}/quotes/${quote.url_slug}`;
    const baseTitle = `${quote.title} | 캐시바이`;
    return (
      <Fragment>
        <Helmet>
          <title>{baseTitle}</title>
          <meta name="description" content={plainTextBody} />
          <link rel="canonical" href={url} />
          <meta property="og:url" content={url} />
          <meta property="og:type" content="article" />
          <meta property="og:title" content={baseTitle} />
          <meta property="og:description" content={plainTextBody} />
          {quote.thumbnail && <meta property="og:image" content={quote.thumbnail} />}
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={baseTitle} />
          <meta name="twitter:description" content={plainTextBody} />
          {quote.thumbnail && <meta name="twitter:image" content={quote.thumbnail} />}
        </Helmet>
        <QuoteLeftSticker
          logged={logged}
          likes={quote.likes}
          liked={quote.liked}
          onToggleLike={this.onToggleLike}
          url={this.props.match.url}
          title={quote.title}
          username={quote.user.username}
          informCopy={this.informCopy}
          disabled={!logged}
        />
        <QuoteToc
          toc={toc}
          activeHeading={activeHeading}
          onActivateHeading={this.onActivateHeading}
        />
        <QuoteHead
          id={quote.id}
          date={quote.released_at}
          title={quote.title}
          tags={quote.tags}
          categories={quote.categories}
          user={quote.user}
          likes={quote.likes}
          liked={quote.liked}
          onToggleLike={this.onToggleLike}
          ownQuote={currentUsername === username}
          onAskRemove={this.onToggleAskRemove}
          logged={logged}
          isPrivate={quote.is_private}
          url={this.props.match.url}
          informCopy={this.informCopy}
        />
        {quote.series && <QuoteSeriesInfo series={quote.series} username={username || ''} />}
        <QuoteContent
          thumbnail={quote.thumbnail}
          body={quote.body}
          onSetToc={onSetToc}
          onActivateHeading={onActivateHeading}
          theme={quote.meta.code_theme}
        />
        <QuoteTags tags={quote.tags} />
        <QuestionModal
          open={askRemove}
          title="견적요청 삭제"
          description="이 견적요청를 정말로 삭제하시겠습니까?"
          confirmText="삭제"
          onConfirm={this.onConfirmRemove}
          onCancel={this.onToggleAskRemove}
        />
      </Fragment>
    );
  }
}

export default connect(
  ({ quotes, pender, user, common }: State) => ({
    currentUsername: user.user && user.user.username,
    quote: quotes.quote,
    toc: quotes.toc,
    activeHeading: quotes.activeHeading,
    likeInProcess: pender.pending['quotes/LIKE'] || pender.pending['quotes/UNLIKE'],
    askRemove: quotes.askRemove,
    routerHistory: common.router.history,
    shouldCancel: common.ssr && !common.router.altered,
    logged: !!user.user,
  }),
  () => ({}),
)(withRouter(QuoteViewer));



// WEBPACK FOOTER //
// ./src/containers/quote/QuoteViewer.js